import type { HttpClient } from '~/shared/api'
import { ApiService, toModel } from '~/shared/api'
import type { Logger, Paginated } from '~/shared/interfaces'
import type { ApiDataResponse, ApiResultResponse } from '~/shared/types/api'
import { PaginationDataModel } from '~/shared/model'
import { AccountResumeModel } from '~/models/account-resumes'
import { ResumeCardModel } from '~/models/resumes'
import { toUnifiedProfile } from '~/models/unified-profile'
import type { UnifiedProfileModel } from '~/models/unified-profile'
import type { ResumesRequestParams } from '~/types/resumes'

type AccountResumesResponse = ApiDataResponse<unknown>

interface ResumesListResponse {
  data?: unknown[]
  meta?: unknown
}

export class ResumesMainService extends ApiService {
  constructor(
    http: HttpClient,
    private readonly logger: Logger,
  ) {
    super(http)
  }

  readonly urls = {
    resumes: '/api/v1/yavagro/resumes',
    query: '/api/v1/yavagro/resumes/query',
    sfQuery: '/api/v1/yavagro/resumes/sf-query',
    profileResumePdf: '/api/v1/yavagro/profile-resume/pdf',
  }

  async getResumesList(
    params: ResumesRequestParams,
    signal?: AbortSignal
  ): Promise<Paginated<ResumeCardModel>> {
    const res = await this.http.request<ResumesListResponse>({
      url: this.urls.query,
      params,
      signal,
    })

    const items = Array.isArray(res.data?.data) ? res.data.data : []

    return {
      models: items.map(item => new ResumeCardModel(item)),
      pagination: new PaginationDataModel(res.data?.meta),
    }
  }

  async getResume(resumeId: number, signal?: AbortSignal): Promise<UnifiedProfileModel> {
    const res = await this.http.request<ApiResultResponse<unknown>>({
      url: `${this.urls.sfQuery}/${resumeId}`,
      signal,
    })

    return toUnifiedProfile(res.data?.result)
  }

  async downloadResumePdf(uuid?: string): Promise<Blob | null> {
    try {
      const res = await this.http.request<Blob>({
        url: uuid ? `${this.urls.profileResumePdf}/${uuid}` : this.urls.profileResumePdf,
        responseType: 'blob',
      })

      return res.data
    } catch (err) {
      this.logger.warn('Failed to download resume pdf', err)
      return null
    }
  }

  async getAccountResumes(): Promise<AccountResumeModel | null> {
    try {
      return await this.http
        .request<AccountResumesResponse>({ url: this.urls.resumes })
        .then(toModel(AccountResumeModel))
    } catch (err) {
      this.logger.warn('Failed to load account resumes', err)
      return null
    }
  }

  async changeResumeStatus(resumeId: number, statusId: string): Promise<void> {
    await this.http.request({
      url: '/api/v1/yavagro/resumes/changeStatus',
      method: 'POST',
      data: { resume: { id: resumeId, status_id: statusId } },
    })
  }
}
